import 'react-quill/dist/quill.bubble.css'

import clsx from 'clsx'
import dynamic from 'next/dynamic'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useRef, useState } from 'react'

import Button from '@/atoms/Button/Button'

import RichTextTrimmerOverlay from './includes/RichTextTrimmerOverlay'

const ReactQuill = dynamic(import('react-quill'), { ssr: false })

function RichTextTrimmer({ value, maxHeight = 160 }: RichTextTrimmerProps) {
  const [height, setHeight] = useState<number | undefined>()
  const [isCollapsed, setCollapsed] = useState(false)

  const { t } = useTranslation('products')
  const buttonText = isCollapsed ? 'global:show_more' : 'global:show_less'

  useEffect(() => {
    setHeight(undefined)
  }, [value])

  const richRef = useRef<HTMLDivElement>()
  useEffect(() => {
    if (richRef.current) {
      const observer = new ResizeObserver((entries) => {
        window.requestAnimationFrame(() => {
          const contentHeight = entries[0].contentRect.height
          if (height === undefined && contentHeight > 0) {
            setHeight(contentHeight)
            setCollapsed(true)
          }
        })
      })

      observer.observe(richRef.current)

      return () => {
        observer.disconnect()
      }
    }
  }, [richRef, height])

  const isOverflowed = !!height && height > maxHeight

  return (
    <div className="flex flex-col justify-center">
      <div ref={richRef as any} className="relative">
        <ReactQuill
          theme={'bubble'}
          value={value}
          style={{
            height: isOverflowed && isCollapsed ? maxHeight : '',
            overflow: 'hidden',
          }}
          readOnly
          preserveWhitespace
        />
        <RichTextTrimmerOverlay isVisible={isOverflowed && isCollapsed} />
      </div>
      {isOverflowed && (
        <Button
          className={clsx(
            'z-10 self-center flex-grow-0 px-6 py-2',
            isCollapsed ? 'mt-1' : 'mt-4'
          )}
          variant="rounded"
          text={t(buttonText)}
          onClick={() => setCollapsed(!isCollapsed)}
        />
      )}
    </div>
  )
}

interface RichTextTrimmerProps {
  value: string
  maxHeight?: number
}

export default RichTextTrimmer
